import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, FileText, Send, Save } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminReports = ({ client }) => {
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [report, setReport] = useState(null);
  const [texts, setTexts] = useState({});
  const [busy, setBusy] = useState(null);

  const build = async () => {
    setBusy("build");
    try {
      const r = await axios.get(`${API}/portal/admin/reports/${client.id}/${month}`, { withCredentials: true });
      setReport(r.data.report);
      setTexts(Object.fromEntries(Object.entries(r.data.report.sections).map(([k, s]) => [k, s.text || ""])));
    } catch {
      toast.error("Could not build the report.");
    } finally {
      setBusy(null);
    }
  };

  const save = async () => {
    setBusy("save");
    try {
      await axios.post(`${API}/portal/admin/reports/${report.id}/save`, { section_texts: texts }, { withCredentials: true });
      toast.success("Report saved.");
    } catch {
      toast.error("Save failed.");
    } finally {
      setBusy(null);
    }
  };

  const send = async () => {
    setBusy("send");
    try {
      await axios.post(`${API}/portal/admin/reports/${report.id}/send`, {}, { withCredentials: true });
      toast.success("Report sent — the client can now see it in their portal.");
      setReport({ ...report, status: "sent" });
    } catch {
      toast.error("Send failed.");
    } finally {
      setBusy(null);
    }
  };

  if (!client) {
    return <p className="text-sm text-slate-500" data-testid="reports-no-client">Select a client in the Clients tab first.</p>;
  }

  const inputCls = "rounded-xl border border-white/15 bg-[#0B162B] px-3 py-2 text-sm text-white outline-none";

  return (
    <div data-testid="admin-reports">
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <span className="font-display font-semibold text-white">{client.name}</span>
        <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className={inputCls} data-testid="report-month-input" />
        <button onClick={build} disabled={busy === "build"} data-testid="report-build-button" className="inline-flex items-center gap-2 rounded-full bg-white px-5 py-2.5 text-sm font-semibold text-[#060D1A] transition-colors duration-300 hover:bg-slate-300 disabled:opacity-50">
          {busy === "build" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
          {report ? "Rebuild from activity log" : "Build report from activity log"}
        </button>
      </div>

      {report && (
        <div className="space-y-4" data-testid="report-builder">
          <p className="text-xs text-slate-500">
            Status: <span className="text-white">{report.status}</span> — built only from verified activity logged this month. Edit any section before sending.
          </p>
          {Object.entries(report.sections).map(([key, s]) => (
            <div key={key} className="rounded-2xl border border-white/10 p-5" data-testid={`report-section-${key}`}>
              <p className="font-mono-label text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-3">{s.title}</p>
              {s.items?.length > 0 && (
                <ul className="space-y-1.5 mb-3">
                  {s.items.map((it, i) => (
                    <li key={i} className="text-sm text-slate-300 flex gap-2">
                      <span className="text-slate-600 shrink-0">{it.date}</span>
                      <span>{it.what}{it.results ? ` — ${it.results}` : ""}</span>
                    </li>
                  ))}
                </ul>
              )}
              <textarea
                rows={key === "work_completed" ? 2 : 3}
                value={texts[key] ?? ""}
                onChange={(e) => setTexts({ ...texts, [key]: e.target.value })}
                placeholder={s.items?.length ? "Optional summary text…" : "No verified activity logged — add notes or leave blank."}
                className="w-full rounded-xl border border-white/15 bg-white/[0.04] px-4 py-3 text-sm text-white leading-relaxed outline-none focus:border-white/40 resize-y placeholder:text-slate-600"
                data-testid={`report-text-${key}`}
              />
            </div>
          ))}
          <div className="flex gap-2 pt-2">
            <button onClick={save} disabled={busy === "save"} data-testid="report-save-button" className="inline-flex items-center gap-2 rounded-full border border-white/15 px-6 py-2.5 text-sm font-semibold text-slate-200 transition-colors duration-300 hover:bg-white hover:text-[#060D1A] disabled:opacity-50">
              {busy === "save" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Save Draft
            </button>
            <button onClick={send} disabled={busy === "send" || report.status === "sent"} data-testid="report-send-button" className="inline-flex items-center gap-2 rounded-full bg-white px-6 py-2.5 text-sm font-semibold text-[#060D1A] transition-colors duration-300 hover:bg-slate-300 disabled:opacity-50">
              {busy === "send" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              {report.status === "sent" ? "Sent to Client" : "Approve & Send to Client"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
